// Call detail (unified, interactionId-keyed).
//
// One call = one TaskDetails row + N TaskLegDetails rows + M AI Agent
// sessions, all joined on interactionId. The AI sessions are usually
// pre-fetched by the search page and passed in via `cachedAiSessions`;
// task + legs are fetched here (serialised with rate-limit sleeps —
// wxccSearchClient paces them 2.2s apart).
//
// For each AI session we need its MESSAGES and AUDIT — those are
// per-session MCP tools, not Search-API queries. We lazy-load those on
// tab change to avoid firing 2M MCP calls on mount.
//
// Home-run additions:
//   - AI Summary auto-fires on first Business-view mount (cached per
//     interactionId+window in sessionStorage; see summaryCache.js).
//   - DiagnoseCard shows "why did this fail" when outcome ∈ {ESCALATED,
//     ABANDONED, FAILED}.
//   - CallerJourney panel surfaces other calls from the same ANI in the
//     search window — zero extra API cost (uses allAiSessions already
//     pulled by resolveCallByInteractionId).
//   - Timeline panel (BusinessView) merges events from THREE sources on
//     one clock: CC-side contact-timeline, per-AI-session messages, and
//     tool invocations within each bot message. Needs ONE new MCP call
//     per call-detail view (`wxcc-operations-get-contact-timeline`) plus
//     the per-session message fetch that ConversationTurns already does
//     — lifted up here to feed both the timeline and the conversation
//     tabs. The timeline is purely presentational (see
//     `components/SessionTimeline.jsx`).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Timeline,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowsTransferUp,
  IconCircleCheck,
  IconCircleX,
  IconClipboardCheck,
  IconHeadset,
  IconListDetails,
  IconLogin,
  IconLogout,
  IconMessageCircle,
  IconPhone,
  IconRobot,
  IconSparkles,
  IconUserCheck,
} from '@tabler/icons-react';

import { useAuth } from '../../../../shared/contexts/AuthContext';
import { callTool } from '../../api_scripts/wxccMcpClient';
import {
  diagnoseCall,
  isWxccAiOpsBackendConfigured,
  summarizeCall,
} from '../../api_scripts/wxccAiOpsClient';
import { resolveCallByInteractionId } from '../../api_scripts/wxccSearchClient';
import { AuditPanel, MessageList } from './sessionPanels';
import { CallerJourney } from './CallerJourney';
import { SessionTimeline } from './components/SessionTimeline';
import { getCachedSummary, setCachedSummary } from './summaryCache';
import {
  Breadcrumbs,
  ErrorAlert,
  HeaderStat,
  OutcomeBadge,
  StatusBadge,
  fmtDuration,
  fmtTs,
  short,
} from './utils';

const CONCERNED_OUTCOMES = new Set(['ESCALATED', 'ABANDONED', 'FAILED']);

export function CallDetail({ interactionId, window: windowProp, cachedAiSessions, setView }) {
  const [resolveState, setResolveState] = useState({ loading: true, data: null, error: null });
  const [viewMode, setViewMode] = useState('business');
  const [summary, setSummary] = useState({ loading: false, text: null, error: null, meta: null });
  const [diagnose, setDiagnose] = useState({ loading: false, text: null, error: null, meta: null });

  // Timeline sources — contact-timeline + per-session messages. Loaded
  // AFTER resolveState so we have the interactionId + session list before
  // firing MCP calls. Rate-limited at 2.2s/req between serial calls to
  // stay under the Cisco OPS throttle. The messages map is also consumed
  // by ConversationTurns (business view) and AiSessionTabContent
  // (technical view) so lifting the fetch here de-dups the work.
  const [contactTimeline, setContactTimeline] = useState({ loading: false, data: null, error: null });
  const [messagesBySession, setMessagesBySession] = useState({ loading: false, data: {}, errors: {} });

  const { getApiToken } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await resolveCallByInteractionId(interactionId, {
          from: windowProp?.from,
          to: windowProp?.to,
          cachedAiSessions,
        });
        if (!cancelled) setResolveState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setResolveState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [interactionId, windowProp?.from, windowProp?.to, cachedAiSessions]);

  const generateSummary = useCallback(async () => {
    setSummary({ loading: true, text: null, error: null, meta: null });
    try {
      const res = await summarizeCall(
        { interactionId, from: windowProp?.from, to: windowProp?.to },
        getApiToken,
      );
      const payload = {
        loading: false,
        text: res.summary,
        error: null,
        meta: { durationMs: res.durationMs, modelId: res.modelId, sourcesUsed: res.sourcesUsed },
      };
      setSummary(payload);
      setCachedSummary(interactionId, windowProp?.from, windowProp?.to, {
        summary: res.summary,
        meta: payload.meta,
      });
    } catch (e) {
      setSummary({ loading: false, text: null, error: e.message, meta: null });
    }
  }, [interactionId, windowProp?.from, windowProp?.to, getApiToken]);

  const generateDiagnose = useCallback(async () => {
    setDiagnose({ loading: true, text: null, error: null, meta: null });
    try {
      const res = await diagnoseCall(
        { interactionId, from: windowProp?.from, to: windowProp?.to },
        getApiToken,
      );
      setDiagnose({
        loading: false,
        text: res.summary ?? res.diagnosis,
        error: null,
        meta: {
          durationMs: res.durationMs,
          modelId: res.modelId,
          patterns: res.patterns ?? [],
          sourcesUsed: res.sourcesUsed,
        },
      });
    } catch (e) {
      setDiagnose({ loading: false, text: null, error: e.message, meta: null });
    }
  }, [interactionId, windowProp?.from, windowProp?.to, getApiToken]);

  // On first mount for a given call, try cache. Nothing in cache? Fire
  // the Business-view summary automatically so the operator doesn't need
  // to click. The cache + the resolve state together prevent double-fires.
  useEffect(() => {
    const cached = getCachedSummary(interactionId, windowProp?.from, windowProp?.to);
    if (cached) {
      setSummary({ loading: false, text: cached.summary, error: null, meta: cached.meta ?? null });
    }
  }, [interactionId, windowProp?.from, windowProp?.to]);

  // Fetch contact-timeline + per-session messages for the Timeline panel.
  // Runs serially with a 2.2s gap to stay under the Cisco OPS cap. Resets
  // when the interactionId or window changes.
  useEffect(() => {
    if (resolveState.loading) return;
    const sessionsForFetch = resolveState.data?.aiSessions ?? [];
    const iid = interactionId;
    if (!iid && sessionsForFetch.length === 0) return;

    let cancelled = false;
    setContactTimeline({ loading: !!iid, data: null, error: null });
    setMessagesBySession({ loading: sessionsForFetch.length > 0, data: {}, errors: {} });

    (async () => {
      // 1. Contact-timeline (one call). Guard with try/catch — a 404 here
      // shouldn't prevent per-session messages from loading.
      if (iid) {
        try {
          const t = await callTool('ops', 'wxcc-operations-get-contact-timeline', { interactionId: iid });
          if (!cancelled) setContactTimeline({ loading: false, data: t, error: null });
        } catch (e) {
          if (!cancelled) setContactTimeline({ loading: false, data: null, error: e.message });
        }
        if (cancelled) return;
        await new Promise((r) => setTimeout(r, 2200));
      }

      // 2. Per-session messages — serialised, 2.2s gap. Each session's
      // messages are stored under its sessionId so TimeLine (and the
      // conversation tabs) can key by session.
      const dataAccumulator = {};
      const errAccumulator = {};
      for (let i = 0; i < sessionsForFetch.length; i += 1) {
        if (cancelled) return;
        const s = sessionsForFetch[i];
        try {
          const res = await callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
            agentId: s.agentId,
            sessionId: s.sessionId,
            limit: 100,
          });
          dataAccumulator[s.sessionId] = res?.messages ?? [];
        } catch (e) {
          errAccumulator[s.sessionId] = e.message;
        }
        // Update after each session so the panel fills in progressively.
        if (!cancelled) {
          setMessagesBySession({
            loading: i < sessionsForFetch.length - 1,
            data: { ...dataAccumulator },
            errors: { ...errAccumulator },
          });
        }
        if (i < sessionsForFetch.length - 1) {
          await new Promise((r) => setTimeout(r, 2200));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [interactionId, resolveState.loading, resolveState.data?.aiSessions]);

  const data = resolveState.data;
  const aiSessions = data?.aiSessions ?? [];
  const task = data?.task;
  const legs = data?.legs ?? [];
  // The "primary" session for the call header's ANI/DNIS. All linked
  // sessions share the same contact, so any row works — pick the first.
  const primary = aiSessions[0] ?? null;

  // Auto-fire the Business-view summary once resolve completes, if the
  // backend is configured, if we haven't already got text/an error, and
  // if nothing is in cache. One-shot: downstream state transitions skip.
  useEffect(() => {
    if (resolveState.loading) return;
    if (!isWxccAiOpsBackendConfigured()) return;
    if (summary.text || summary.loading || summary.error) return;
    if (!primary) return;
    generateSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveState.loading, primary?.sessionId]);

  const outcome = primary?.outcome;
  const showDiagnose = outcome && CONCERNED_OUTCOMES.has(outcome);

  const allAiSessions = data?.allAiSessions ?? [];
  const callerAni = primary?.ani ?? null;

  return (
    <Stack>
      <Group justify="space-between" align="center">
        <Breadcrumbs
          items={[
            { label: 'Call search', onClick: () => setView({ name: 'calls' }) },
            { label: short(interactionId) },
          ]}
        />
        <SegmentedControl
          size="xs"
          value={viewMode}
          onChange={setViewMode}
          data={[
            { label: 'Business', value: 'business' },
            { label: 'Technical', value: 'technical' },
          ]}
        />
      </Group>

      <CallHeader
        interactionId={interactionId}
        primary={primary}
        task={task}
        aiSessions={aiSessions}
        viewMode={viewMode}
        summary={summary}
        onGenerateSummary={generateSummary}
      />

      {resolveState.loading && <Loader size="sm" />}
      {resolveState.error && <ErrorAlert message={resolveState.error} />}
      {data?.errors && viewMode === 'technical' && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Partial data">
          Some roots failed to fetch:
          <Code block mt="xs">{JSON.stringify(data.errors, null, 2)}</Code>
        </Alert>
      )}

      {!resolveState.loading && viewMode === 'business' && (
        <BusinessView
          primary={primary}
          task={task}
          legs={legs}
          aiSessions={aiSessions}
          allAiSessions={allAiSessions}
          callerAni={callerAni}
          interactionId={interactionId}
          window={windowProp}
          setView={setView}
          summary={summary}
          onGenerateSummary={generateSummary}
          showDiagnose={showDiagnose}
          diagnose={diagnose}
          onGenerateDiagnose={generateDiagnose}
          contactTimeline={contactTimeline}
          messagesBySession={messagesBySession}
        />
      )}

      {!resolveState.loading && viewMode === 'technical' && (
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <CcSidePanel task={task} legs={legs} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <AiSessionsPanel aiSessions={aiSessions} setView={setView} />
          </Grid.Col>
        </Grid>
      )}
    </Stack>
  );
}

// ─── Business view (CC Supervisor audience) ─────────────────────────────
//
// No raw IDs, no audit JSON blobs, no message list. Just: who called,
// what service area, how long, what happened in plain-English events,
// and the final outcome. The AI Summary (when generated via the header
// button) sits above this — it IS the headline for this audience.

function BusinessView({
  primary,
  task,
  legs,
  aiSessions,
  allAiSessions,
  callerAni,
  interactionId,
  window: windowProp,
  setView,
  summary,
  onGenerateSummary,
  showDiagnose,
  diagnose,
  onGenerateDiagnose,
  contactTimeline,
  messagesBySession,
}) {
  const events = buildCallStory({ primary, task, legs, aiSessions });

  return (
    <Stack gap="md">
      <AiSummaryCard summary={summary} onGenerate={onGenerateSummary} variant="business" />

      {showDiagnose && (
        <DiagnoseCard
          diagnose={diagnose}
          onGenerate={onGenerateDiagnose}
          outcome={primary?.outcome}
        />
      )}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="md" radius="md">
            <Title order={5} mb="md">Call flow</Title>
            {events.length === 0 ? (
              <Text size="sm" c="dimmed">No call events available.</Text>
            ) : (
              <Timeline active={events.length - 1} bulletSize={24} lineWidth={2}>
                {events.map((e, i) => (
                  <Timeline.Item
                    key={i}
                    bullet={
                      <ThemeIcon size={24} radius="xl" color={e.color}>
                        <e.Icon size={14} />
                      </ThemeIcon>
                    }
                    title={e.title}
                  >
                    <Text size="xs" c="dimmed">{e.when}</Text>
                    {e.detail && <Text size="sm" mt={2}>{e.detail}</Text>}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <Paper withBorder p="md" radius="md">
              <Title order={5} mb="xs">Outcome</Title>
              <Group gap="xs" mb="sm">
                <OutcomeBadge outcome={primary?.outcome} />
                {task && (
                  <Text size="xs" c="dimmed">
                    CC: {task.terminationType ?? '—'}
                    {task.isHandled ? ' · handled' : ' · not handled'}
                  </Text>
                )}
              </Group>
              <Text size="sm">{plainOutcomeSentence(primary, task)}</Text>
            </Paper>

            {aiSessions.length > 0 && (
              <Paper withBorder p="md" radius="md">
                <Title order={5} mb="xs">AI Agents involved ({aiSessions.length})</Title>
                <Stack gap="xs">
                  {aiSessions.map((s, i) => (
                    <div key={s.sessionId}>
                      <Group gap="xs" wrap="nowrap">
                        <ThemeIcon size="sm" variant="light" color="blue">
                          <IconRobot size={12} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>{s.agentName || `Bot ${i + 1}`}</Text>
                        <Badge size="xs" variant="light" color={s.agentType === 'AUTONOMOUS' ? 'blue' : 'grape'}>
                          {s.agentType}
                        </Badge>
                      </Group>
                      <Text size="xs" c="dimmed" ml={28}>
                        {s.messageCount ?? 0} msgs · {fmtDuration(s.durationMs)}
                        {s.lastAction ? ` · last: ${s.lastAction}` : ''}
                      </Text>
                    </div>
                  ))}
                </Stack>
              </Paper>
            )}

            {task?.lastAgent && (
              <Paper withBorder p="md" radius="md">
                <Title order={5} mb="xs">Routed to a human agent</Title>
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="teal">
                    <IconHeadset size={12} />
                  </ThemeIcon>
                  <Text size="sm">
                    {task.lastAgent}
                    {task.lastTeam ? ` (${task.lastTeam})` : ''}
                  </Text>
                </Group>
                {task.wrapupCode && (
                  <Text size="xs" c="dimmed" mt="xs">Wrap-up: {task.wrapupCode}</Text>
                )}
              </Paper>
            )}

            <CallerJourney
              callerAni={callerAni}
              allAiSessions={allAiSessions}
              interactionId={interactionId}
              window={windowProp}
              setView={setView}
            />
          </Stack>
        </Grid.Col>
      </Grid>

      <TimelinePanel
        contactTimeline={contactTimeline}
        messagesBySession={messagesBySession}
        aiSessions={aiSessions}
      />

      {aiSessions.length > 0 && (
        <ConversationPanel aiSessions={aiSessions} messagesBySession={messagesBySession} />
      )}
    </Stack>
  );
}

// ─── Timeline panel (Business view) ─────────────────────────────────────
//
// Vertical merged list of events across the three sources — CC-timeline,
// per-session bot/human turns, tool invocations. Thin wrapper around
// SessionTimeline so BusinessView stays declarative.

function TimelinePanel({ contactTimeline, messagesBySession, aiSessions }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs" mb="xs">
        <IconListDetails size={18} color="#1976d2" />
        <Title order={5}>Timeline</Title>
        <Text size="xs" c="dimmed">
          CC events + bot / caller turns + tool invocations, sorted by timestamp. Click a row to expand the raw payload.
        </Text>
      </Group>
      <SessionTimeline
        contactTimeline={contactTimeline?.data}
        contactTimelineLoading={!!contactTimeline?.loading}
        contactTimelineError={contactTimeline?.error}
        messagesBySession={messagesBySession?.data ?? {}}
        messagesBySessionLoading={!!messagesBySession?.loading}
        aiSessions={aiSessions}
      />
    </Paper>
  );
}

// ─── Conversation panel (Business view) ─────────────────────────────────

function ConversationPanel({ aiSessions, messagesBySession }) {
  const [activeTab, setActiveTab] = useState(aiSessions[0]?.sessionId ?? null);
  useEffect(() => {
    if (!activeTab && aiSessions[0]) setActiveTab(aiSessions[0].sessionId);
  }, [aiSessions, activeTab]);

  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs" mb="xs">
        <IconMessageCircle size={18} color="#1976d2" />
        <Title order={5}>Conversation detail</Title>
      </Group>
      <Alert color="gray" variant="light" mb="sm" icon={<IconAlertTriangle size={14} />}>
        Message content is end-to-end encrypted by Webex KMS, so the actual words spoken
        aren't visible here. What's shown below: who spoke, when, which bot tools fired,
        and how fast each turn responded.
      </Alert>

      {aiSessions.length === 1 ? (
        <ConversationTurns session={aiSessions[0]} messagesBySession={messagesBySession} />
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            {aiSessions.map((s, i) => (
              <Tabs.Tab key={s.sessionId} value={s.sessionId} leftSection={<IconRobot size={12} />}>
                {s.agentName || `Bot ${i + 1}`}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {aiSessions.map((s) => (
            <Tabs.Panel key={s.sessionId} value={s.sessionId} pt="sm">
              <ConversationTurns session={s} messagesBySession={messagesBySession} />
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Paper>
  );
}

// ConversationTurns prefers the lifted messagesBySession from CallDetail
// — same fetch feeds the Timeline panel. Falls back to its own fetch when
// (a) the parent hasn't delivered that session's messages yet AND
// (b) the parent isn't actively loading them (unmounted CallDetail reload,
// or the parent fetch errored out for this session). Prevents the
// double-fetch race the earlier version had.
function ConversationTurns({ session, messagesBySession }) {
  const parentMessages = messagesBySession?.data?.[session.sessionId];
  const parentError = messagesBySession?.errors?.[session.sessionId];
  const parentLoading = !!messagesBySession?.loading;

  const [state, setState] = useState({ loading: !parentMessages && !parentError, data: null, error: null });

  useEffect(() => {
    // If the parent has (or is loading) this session's messages, skip
    // the local fetch — just mirror what the parent gave us.
    if (parentMessages) {
      setState({ loading: false, data: { messages: parentMessages }, error: null });
      return;
    }
    if (parentError) {
      setState({ loading: false, data: null, error: parentError });
      return;
    }
    if (parentLoading) {
      setState({ loading: true, data: null, error: null });
      return;
    }

    // Parent didn't provide this session's messages and isn't actively
    // loading them — fall back to the local fetch.
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    (async () => {
      try {
        const res = await callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
          agentId: session.agentId,
          sessionId: session.sessionId,
          limit: 50,
        });
        if (!cancelled) setState({ loading: false, data: res, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [session.agentId, session.sessionId, parentMessages, parentError, parentLoading]);

  if (state.loading) return <Loader size="sm" />;
  if (state.error) return <ErrorAlert message={state.error} />;
  const messages = state.data?.messages ?? [];
  if (messages.length === 0) {
    return <Text size="sm" c="dimmed">No turns recorded for this session.</Text>;
  }

  return (
    <Stack gap="xs">
      {messages.map((m, i) => (
        <ConversationTurn key={m.id ?? i} m={m} index={i} />
      ))}
    </Stack>
  );
}

function ConversationTurn({ m, index }) {
  const role = m.userType ?? 'unknown';
  const roleLabel = role === 'bot' ? 'Bot' : role === 'human' ? 'Caller' : role === 'custom_data' ? 'System event' : role;
  const roleColor = role === 'bot' ? 'blue' : role === 'human' ? 'teal' : 'gray';
  const roleIcon = role === 'bot' ? IconRobot : role === 'human' ? IconHeadset : IconClipboardCheck;
  const RoleIcon = roleIcon;

  const meta = m.metadata ?? {};
  const tools = Array.isArray(meta.tools) ? meta.tools : [];
  const classifications = Array.isArray(meta.classification) ? meta.classification : [];
  const latency = typeof meta.latency === 'number' ? meta.latency : null;

  // Build a plain-English per-turn description from the unencrypted metadata.
  const descriptions = [];
  if (tools.length) {
    const names = tools
      .map((t) => (typeof t === 'string' ? t : t?.name ?? t?.tool ?? null))
      .filter(Boolean);
    if (names.length) descriptions.push(`invoked ${names.join(', ')}`);
  }
  if (classifications.length) {
    const names = classifications
      .map((c) => (typeof c === 'string' ? c : c?.intent ?? c?.name ?? null))
      .filter(Boolean);
    if (names.length) descriptions.push(`classified as ${names.join(', ')}`);
  }
  if (role === 'bot' && !descriptions.length && m.text) descriptions.push('spoke (encrypted)');
  if (role === 'human' && !descriptions.length) descriptions.push('spoke (encrypted)');

  return (
    <Paper withBorder p="xs" radius="sm" style={{ background: role === 'bot' ? '#f6f9ff' : role === 'human' ? '#f4faf7' : '#fafafa' }}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <ThemeIcon size="sm" variant="light" color={roleColor}>
          <RoleIcon size={12} />
        </ThemeIcon>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Text size="xs" fw={600}>Turn {index + 1} · {roleLabel}</Text>
            <Text size="xs" c="dimmed">
              {fmtTs(m.createdAt, true)}
              {latency ? ` · ${(latency / 1000).toFixed(1)}s to respond` : ''}
            </Text>
          </Group>
          {descriptions.length > 0 && (
            <Text size="sm" mt={2}>{descriptions.join(' · ')}</Text>
          )}
        </div>
      </Group>
    </Paper>
  );
}

// Compose an ordered, non-technical event list from the three data sources.
// Only facts that have business meaning — no IDs, no latency ms, no
// userType sequence.
function buildCallStory({ primary, task, legs, aiSessions }) {
  const items = [];

  const arrivalTs = task?.createdTs ?? primary?.startTs;
  if (arrivalTs) {
    items.push({
      ts: arrivalTs,
      when: fmtTs(arrivalTs),
      Icon: IconLogin,
      color: 'blue',
      title: 'Call arrived',
      detail:
        primary
          ? `From ${primary.ani || 'unknown'}${primary.entryPoint ? ` · entry point: ${primary.entryPoint}` : ''}`
          : null,
    });
  }

  const sortedAi = [...aiSessions].sort((a, b) => (a.startTs ?? 0) - (b.startTs ?? 0));
  for (const s of sortedAi) {
    items.push({
      ts: s.startTs,
      when: fmtTs(s.startTs),
      Icon: IconRobot,
      color: 'indigo',
      title: `${s.agentName || 'AI Agent'} engaged`,
      detail: [
        s.agentType ? `${s.agentType.toLowerCase()} bot` : null,
        s.messageCount != null ? `${s.messageCount} message${s.messageCount === 1 ? '' : 's'}` : null,
        s.lastAction ? `last action: ${s.lastAction}` : null,
      ].filter(Boolean).join(' · ') || null,
    });
    if (s.outcome === 'ESCALATED') {
      items.push({
        ts: s.endTs ?? s.startTs,
        when: fmtTs(s.endTs ?? s.startTs),
        Icon: IconArrowsTransferUp,
        color: 'orange',
        title: `${s.agentName || 'Bot'} escalated the call`,
      });
    }
    if (s.outcome === 'ABANDONED') {
      items.push({
        ts: s.endTs ?? s.startTs,
        when: fmtTs(s.endTs ?? s.startTs),
        Icon: IconCircleX,
        color: 'orange',
        title: `Caller abandoned during ${s.agentName || 'bot'}`,
      });
    }
  }

  const connectedLeg = legs.find((l) => l.owner);
  if (connectedLeg) {
    items.push({
      ts: connectedLeg.createdTs,
      when: fmtTs(connectedLeg.createdTs),
      Icon: IconUserCheck,
      color: 'teal',
      title: `Connected to ${connectedLeg.owner}`,
      detail: [
        connectedLeg.queue ? `queue: ${connectedLeg.queue}` : null,
        connectedLeg.team ? `team: ${connectedLeg.team}` : null,
        connectedLeg.handleTimeMs ? `handled for ${fmtDuration(connectedLeg.handleTimeMs)}` : null,
      ].filter(Boolean).join(' · ') || null,
    });
  }

  const endTs = task?.endedTs ?? primary?.endTs;
  if (endTs) {
    const outcomeIcon =
      primary?.outcome === 'HANDLED' || task?.isHandled ? IconCircleCheck :
      primary?.outcome === 'ABANDONED' ? IconCircleX :
      IconLogout;
    const outcomeColor =
      primary?.outcome === 'HANDLED' || task?.isHandled ? 'teal' :
      primary?.outcome === 'ABANDONED' ? 'orange' : 'gray';
    items.push({
      ts: endTs,
      when: fmtTs(endTs),
      Icon: outcomeIcon,
      color: outcomeColor,
      title: 'Call ended',
      detail: [
        task?.terminationType ? task.terminationType : null,
        task?.terminationReason ? task.terminationReason : null,
      ].filter(Boolean).join(' · ') || null,
    });
  }

  return items.sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));
}

// One-sentence plain-English outcome for the right-column outcome card.
function plainOutcomeSentence(primary, task) {
  if (!primary && !task) return 'No outcome data available for this call.';
  const outcome = primary?.outcome;
  const handledByBot = outcome === 'HANDLED' || outcome === 'COMPLETED';
  const abandoned = outcome === 'ABANDONED';
  const escalated = outcome === 'ESCALATED' || !!task?.lastAgent;

  if (handledByBot && !task?.lastAgent) {
    return `The AI Agent handled this call without transferring to a human.`;
  }
  if (abandoned) {
    return `The caller hung up before the call was resolved.`;
  }
  if (escalated && task?.lastAgent) {
    return `The AI Agent handed the call off to a human agent (${task.lastAgent}), who then ${task.isHandled ? 'completed the interaction' : 'did not finalise wrap-up'}.`;
  }
  if (escalated) {
    return `The AI Agent escalated the call, but no human agent was recorded on the contact — likely routed to a queue the caller abandoned or that didn't log an agent.`;
  }
  return `Call ended with outcome: ${outcome || task?.terminationType || 'unknown'}.`;
}

function CallHeader({ interactionId, primary, task, aiSessions, viewMode = 'business', summary, onGenerateSummary }) {
  const configured = isWxccAiOpsBackendConfigured();
  const ani = primary?.ani ?? '—';
  const dnis = primary?.dnis ?? '—';
  const startTs = primary?.startTs ?? task?.createdTs;
  const duration = task?.durationMs ?? primary?.durationMs;
  const technical = viewMode === 'technical';

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" align="flex-start" mb="sm">
        <Stack gap={4}>
          <Group gap="xs">
            <IconPhone size={18} color="#1976d2" />
            <Title order={4}>
              {technical ? `Call ${short(interactionId)}` : `Call from ${ani}`}
            </Title>
            {task && <StatusBadge status={task.status} />}
          </Group>
          {technical ? (
            <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
              interactionId {interactionId}
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              {primary?.entryPoint ? `${primary.entryPoint} · ` : ''}
              {fmtTs(startTs)}
            </Text>
          )}
        </Stack>
        <Button
          size="xs"
          leftSection={<IconSparkles size={14} />}
          onClick={onGenerateSummary}
          loading={summary?.loading}
          disabled={!configured}
        >
          {summary?.text ? 'Regenerate AI Summary' : 'Generate AI Summary'}
        </Button>
      </Group>

      <Grid gutter="sm">
        <HeaderStat label="Caller (ANI)" value={ani} mono />
        <HeaderStat label="Dialed (DNIS)" value={dnis} mono />
        <HeaderStat label="Started" value={fmtTs(startTs)} />
        <HeaderStat label="Duration" value={fmtDuration(duration)} />
        {technical && (
          <>
            <HeaderStat label="Direction" value={task?.direction ?? primary?.channelType ?? '—'} />
            <HeaderStat label="Bots on call" value={aiSessions.length} />
            <HeaderStat label="Outcome" value={<OutcomeBadge outcome={primary?.outcome} />} raw />
            <HeaderStat label="Termination" value={task?.terminationType ?? '—'} />
          </>
        )}
        {!technical && (
          <>
            <HeaderStat label="Service area" value={primary?.entryPoint || task?.entryPoints?.[0] || '—'} />
            <HeaderStat label="Outcome" value={<OutcomeBadge outcome={primary?.outcome} />} raw />
          </>
        )}
      </Grid>

      {!configured && (
        <Text size="xs" c="dimmed" mt="xs">
          AI Summary unavailable — REACT_APP_CCUTILS_API_BASE_URL not set.
        </Text>
      )}
    </Paper>
  );
}

// Renders the AI Summary panel. In Business mode it's a full-width card
// sitting between the header and the timeline. In Technical mode the
// callers can place it wherever makes sense.
function AiSummaryCard({ summary, onGenerate, variant = 'business' }) {
  const configured = isWxccAiOpsBackendConfigured();
  const big = variant === 'business';

  return (
    <Paper
      withBorder
      p={big ? 'md' : 'sm'}
      radius="md"
      style={{ background: big ? 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)' : '#f8fbff' }}
    >
      <Group justify="space-between" mb={big ? 'xs' : 4}>
        <Group gap="xs">
          <IconSparkles size={big ? 18 : 14} color="#1976d2" />
          <Title order={big ? 5 : 6}>AI Summary</Title>
          {summary?.meta?.durationMs != null && (
            <Text size="xs" c="dimmed">generated in {(summary.meta.durationMs / 1000).toFixed(1)}s</Text>
          )}
        </Group>
        {!summary?.text && !summary?.loading && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconSparkles size={14} />}
            onClick={onGenerate}
            disabled={!configured}
          >
            Generate
          </Button>
        )}
      </Group>

      {summary?.loading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">Reading audit, messages, and call context — this takes ~15–30 seconds for a multi-bot call.</Text>
        </Group>
      )}

      {summary?.error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {summary.error}
        </Alert>
      )}

      {!summary?.loading && !summary?.text && !summary?.error && (
        <Text size="sm" c="dimmed">
          Click Generate to produce a plain-English narrative of what happened on this call.
          The summary pulls from audit findings, bot tool invocations, and CC-side routing — it does not read
          transcript text (end-to-end encrypted).
        </Text>
      )}

      {summary?.text && (
        <Text size={big ? 'sm' : 'xs'} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
          {summary.text}
        </Text>
      )}
    </Paper>
  );
}

function DiagnoseCard({ diagnose, onGenerate, outcome }) {
  const configured = isWxccAiOpsBackendConfigured();
  const palette = diagnoseColor(outcome);
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" radius="xl" color={palette.themeColor} variant="filled">
            <IconAlertTriangle size={12} />
          </ThemeIcon>
          <Title order={5}>Why did this fail?</Title>
          {diagnose?.meta?.patterns?.length > 0 && (
            <Group gap={4}>
              {diagnose.meta.patterns.slice(0, 4).map((p) => (
                <Badge key={p} size="xs" color={palette.themeColor} variant="light">
                  {p}
                </Badge>
              ))}
            </Group>
          )}
        </Group>
        {!diagnose?.text && !diagnose?.loading && (
          <Button
            size="xs"
            variant="light"
            color={palette.themeColor}
            leftSection={<IconSparkles size={14} />}
            onClick={onGenerate}
            disabled={!configured}
          >
            Diagnose
          </Button>
        )}
      </Group>

      {diagnose?.loading && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Scanning this call against known failure patterns — ~15 seconds.
          </Text>
        </Group>
      )}

      {diagnose?.error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {diagnose.error}
        </Alert>
      )}

      {!diagnose?.loading && !diagnose?.text && !diagnose?.error && (
        <Text size="sm" c="dimmed">
          This call ended as <strong>{outcome}</strong>. Click <em>Diagnose</em> for a
          targeted root-cause pass using the same metadata the summarizer sees,
          plus a catalog of common WxCC failure patterns.
        </Text>
      )}

      {diagnose?.text && (
        <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
          {diagnose.text}
        </Text>
      )}
    </Paper>
  );
}

function diagnoseColor(outcome) {
  if (outcome === 'FAILED') {
    return { bg: '#fff5f5', border: '#f8c3c3', themeColor: 'red' };
  }
  if (outcome === 'ABANDONED') {
    return { bg: '#fff8f0', border: '#f8d4a3', themeColor: 'orange' };
  }
  // ESCALATED
  return { bg: '#fff8e1', border: '#f0e0a8', themeColor: 'yellow' };
}

function CcSidePanel({ task, legs }) {
  return (
    <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
      <Title order={5} mb="xs">Contact Center side</Title>
      {!task && (
        <Alert color="gray">
          No TaskDetails row for this interactionId in the fetched window. The call may be older
          than the search window, or this tenant may not surface it under taskDetails.
        </Alert>
      )}
      {task && (
        <>
          <Grid gutter="xs">
            <HeaderStat label="Entry point" value={task.entryPoints?.join(', ') || '—'} />
            <HeaderStat label="First queue" value={task.firstQueue || '—'} />
            <HeaderStat label="Last queue" value={task.lastQueue || '—'} />
            <HeaderStat label="Last agent" value={task.lastAgent || '—'} />
            <HeaderStat label="Last team" value={task.lastTeam || '—'} />
            <HeaderStat label="Last site" value={task.lastSite || '—'} />
            <HeaderStat label="Termination reason" value={task.terminationReason || '—'} />
            <HeaderStat label="Handled?" value={task.isHandled ? 'yes' : 'no'} />
          </Grid>

          <Divider my="sm" />
          <Text size="xs" fw={600} mb={4}>Legs ({legs.length})</Text>
          {legs.length === 0 ? (
            <Text size="xs" c="dimmed">No legs recorded.</Text>
          ) : (
            <Stack gap={4}>
              {legs.map((leg) => (
                <Paper key={leg.legId} withBorder p="xs" radius="sm" style={{ background: '#fafafa' }}>
                  <Group gap="xs" justify="space-between">
                    <Text size="xs" fw={500}>{leg.callLegType || leg.handleType || 'leg'}</Text>
                    <Text size="xs" c="dimmed">{fmtTs(leg.createdTs, true)} · {fmtDuration(leg.handleTimeMs)}</Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    queue: {leg.queue || '—'} · owner: {leg.owner || '—'}
                    {leg.terminationReason ? ` · ${leg.terminationReason}` : ''}
                  </Text>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}

function AiSessionsPanel({ aiSessions, setView }) {
  const [activeTab, setActiveTab] = useState(aiSessions[0]?.sessionId ?? null);
  // Keep activeTab in sync when sessions arrive after mount.
  useEffect(() => {
    if (!activeTab && aiSessions[0]) setActiveTab(aiSessions[0].sessionId);
  }, [aiSessions, activeTab]);

  if (aiSessions.length === 0) {
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="xs">AI Agent sessions</Title>
        <Alert color="gray">No AI Agent sessions linked to this interactionId.</Alert>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>AI Agent sessions ({aiSessions.length})</Title>
      </Group>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          {aiSessions.map((s, i) => (
            <Tabs.Tab key={s.sessionId} value={s.sessionId} leftSection={<IconRobot size={12} />}>
              {s.agentName || `Bot ${i + 1}`}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {aiSessions.map((s) => (
          <Tabs.Panel key={s.sessionId} value={s.sessionId} pt="sm">
            <AiSessionTabContent session={s} setView={setView} />
          </Tabs.Panel>
        ))}
      </Tabs>
    </Paper>
  );
}

function AiSessionTabContent({ session, setView }) {
  const [audit, setAudit] = useState({ loading: true, data: null, error: null });
  const [msgs, setMsgs] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    // Serialise: the OPS endpoint rate-limits at ~1 req / 2s. If we fired
    // these in parallel the second call always comes back as
    // "S1 Tool rate limit exceeded" and the panel errors.
    let cancelled = false;
    (async () => {
      try {
        const auditRes = await callTool('ops', 'wxcc-operations-audit-ai-agent', {
          agentId: session.agentId,
          sessionId: session.sessionId,
          scope: 'exactSession',
        });
        if (cancelled) return;
        setAudit({ loading: false, data: auditRes, error: null });
      } catch (e) {
        if (!cancelled) setAudit({ loading: false, data: null, error: e.message });
      }
      await new Promise((r) => setTimeout(r, 2200));
      if (cancelled) return;
      try {
        const msgsRes = await callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
          agentId: session.agentId,
          sessionId: session.sessionId,
          limit: 50,
        });
        if (!cancelled) setMsgs({ loading: false, data: msgsRes, error: null });
      } catch (e) {
        if (!cancelled) setMsgs({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [session.agentId, session.sessionId]);

  return (
    <Stack gap="sm">
      <Group gap="xs">
        <Badge size="xs" variant="light" color={session.agentType === 'AUTONOMOUS' ? 'blue' : 'grape'}>
          {session.agentType || 'bot'}
        </Badge>
        {session.channelType && <Badge size="xs" variant="outline">{session.channelType}</Badge>}
        <OutcomeBadge outcome={session.outcome} />
        <Text size="xs" c="dimmed">
          {fmtTs(session.startTs)} · {fmtDuration(session.durationMs)} · {session.messageCount ?? '?'} msgs
        </Text>
        <Anchor
          size="xs"
          onClick={() =>
            setView({
              name: 'session',
              agentId: session.agentId,
              agentName: session.agentName,
              sessionId: session.sessionId,
            })
          }
          style={{ cursor: 'pointer', marginLeft: 'auto' }}
        >
          Open in agent view →
        </Anchor>
      </Group>

      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group gap={4} mb={4}>
            <IconClipboardCheck size={14} />
            <Text size="xs" fw={600}>Audit</Text>
          </Group>
          <AuditPanel loading={audit.loading} error={audit.error} data={audit.data} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Group gap={4} mb={4}>
            <IconMessageCircle size={14} />
            <Text size="xs" fw={600}>Messages</Text>
          </Group>
          <MessageList loading={msgs.loading} error={msgs.error} data={msgs.data} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
